/*
 * ReferralForm.jsx — public shareable form (no auth). Route: /refer/:token
 *
 * Iter 163. Rendered OUTSIDE the Layout so we don't leak any sidebar/nav
 * to an anonymous referring provider. Mobile-first, single-column so the
 * whole form is thumb-friendly on the phone in the referring practice.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle, ShieldCheck, FileDown, Bookmark, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IS_LUDO = new URLSearchParams(window.location.search).get('org') === 'ludo';
const QS = IS_LUDO ? '?org=ludo' : '';

const BRAND = IS_LUDO
  ? { name: 'Ludo ABA', short: 'Ludo ABA', service: 'ABA Assessment',
      services: ['ABA Assessment', 'ABA Therapy (direct)', 'Parent Training', 'Other'],
      reasonHint: 'E.g. autism diagnosis, behavior concerns, social or communication skill delays.',
      ack: 'I certify this referral is medically appropriate. I authorize Ludo ABA to contact the patient / family to schedule an ABA assessment.',
      clinicLabel: 'Location', clinicHint: 'Ludo ABA center.',
      logo: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b1a1b908-c6f4-4002-b0a6-ade01782df2b/artifacts/94a8114fcdc3d511_LUDO.webp' }
  : { name: 'Thrive Speech Therapy', short: 'Thrive', service: 'Initial Speech-Language Evaluation',
      services: ['Initial Speech-Language Evaluation', 'Feeding / Swallowing Evaluation', 'Re-evaluation', 'Other'],
      reasonHint: 'E.g. delayed expressive language, unclear articulation, parental concern.',
      ack: 'I certify this referral is medically appropriate. I authorize Thrive Speech Therapy to contact the patient / family to schedule an evaluation.',
      clinicLabel: 'Thrive clinic location', clinicHint: 'Pre-selected based on the referral link. Change if a different clinic is closer.',
      logo: 'https://customer-assets-jai6qajn.emergentagent.net/job_pediatric-dispatch/artifacts/ocaqpxqe___circle%20logo_dark%20purple.jpg' };
const LOGO = BRAND.logo;
const PAGE_BG = IS_LUDO ? 'linear-gradient(180deg, #f7f8f7 0%, #ede9f4 100%)' : 'linear-gradient(180deg, #FBF8F4 0%, #F6F1EE 100%)';

function Field({ label, required, children, hint, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-sm font-medium text-[#2D2833] mb-1.5">
        {label}{required && <span className="text-[#B54B4B]"> *</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-[#685B72] mt-1">{hint}</span>}
    </label>
  );
}

const inputCls =
  'w-full h-11 rounded-lg border border-[#E4DBE8] bg-white px-3 text-sm text-[#2D2833] ' +
  'outline-none focus:border-[#685B72] focus:ring-2 focus:ring-[#BAA9CA]/30 transition-colors';
const textareaCls =
  'w-full min-h-24 rounded-lg border border-[#E4DBE8] bg-white p-3 text-sm text-[#2D2833] ' +
  'outline-none focus:border-[#685B72] focus:ring-2 focus:ring-[#BAA9CA]/30 transition-colors resize-y';

export default function ReferralForm() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [done, setDone] = useState(null);
  const [showInsurance, setShowInsurance] = useState(false);

  const [form, setForm] = useState({
    patient_name: '', patient_dob: '', guardian_name: '', guardian_phone: '',
    provider_name: '', practice_name: '', provider_npi: '', provider_phone: '', provider_fax: '', provider_email: '',
    signature_typed: '', acknowledged: false,
    requested_service: BRAND.service, service_other: '',
    reason_for_referral: '', medical_history: '', icd10_code: '',
    clinic: '',
    insurance_company: '', insurance_member_id: '', insurance_group: '',
    policyholder_name: '', policyholder_dob: '', policyholder_relationship: '',
  });

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/referral/${token}${QS}`)
      .then(r => { if (alive) { setMeta(r.data); setForm(f => ({ ...f, clinic: r.data.clinic })); } })
      .catch(e => { if (alive) setLoadErr(e?.response?.data?.detail || 'This referral link is invalid.'); });
    return () => { alive = false; };
  }, [token]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canSubmit = useMemo(() => {
    return form.patient_name.trim() && form.patient_dob.trim() && form.guardian_name.trim()
      && form.guardian_phone.trim() && form.provider_name.trim() && form.practice_name.trim()
      && form.signature_typed.trim() && form.acknowledged && form.reason_for_referral.trim()
      && form.clinic;
  }, [form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitErr('');
    try {
      // Merge service_other into requested_service if user picked "Other"
      const payload = { ...form };
      if (form.requested_service === 'Other' && form.service_other.trim()) {
        payload.requested_service = form.service_other.trim();
      }
      delete payload.service_other;
      const r = await axios.post(`${API}/referral/${token}/submit${QS}`, payload);
      setDone(r.data);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 429) {
        setSubmitErr("You've submitted too many referrals from this network in a short time. Please wait a few minutes and try again.");
      } else {
        setSubmitErr(e?.response?.data?.detail || 'Something went wrong. Please try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Loading / error / success shells ───
  if (loadErr) {
    return (
      <ShellCentered>
        <div className="flex flex-col items-center gap-3 text-center" data-testid="referral-form-invalid">
          <AlertTriangle className="text-[#B54B4B]" size={40} />
          <h1 className="font-serif text-2xl text-[#2D2833]">Link not available</h1>
          <p className="text-sm text-[#685B72] max-w-md">{loadErr}</p>
          <p className="text-xs text-[#685B72] mt-2">Please contact the clinic for an updated referral link.</p>
        </div>
      </ShellCentered>
    );
  }
  if (!meta) {
    return (
      <ShellCentered>
        <div className="flex items-center gap-2 text-[#685B72]"><Loader2 className="animate-spin" size={18} /> Loading referral form…</div>
      </ShellCentered>
    );
  }
  if (done) {
    const posters = done.posters || {};
    const linkUrl = done.referral_link_url || '';
    const copyLink = () => {
      if (!linkUrl) return;
      navigator.clipboard?.writeText(linkUrl).then(
        () => { /* silent — button label flips below */ },
        () => {},
      );
    };
    return (
      <div className="min-h-screen" style={{ background: PAGE_BG }}>
        <header className="border-b border-white/50 backdrop-blur-md bg-white/70 sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
            <img src={LOGO} alt={BRAND.short} className="w-11 h-11 rounded-full ring-1 ring-[#BAA9CA]/40" />
            <div>
              <div className="font-serif text-xl leading-none text-[#2D2833]">{BRAND.name}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#685B72] mt-1 font-semibold">Referral received</div>
            </div>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-5 py-10 pb-24" data-testid="referral-form-success">
          {/* ── Confirmation card ─────────────────────────────────── */}
          <section className="bg-white/85 rounded-2xl border border-white/60 shadow-sm p-6 sm:p-8 text-center">
            <CheckCircle2 className="text-[#4E7A6A] mx-auto" size={56} />
            <h1 className="font-serif text-3xl sm:text-4xl text-[#2D2833] mt-3">Thank you for the referral</h1>
            <p className="text-sm text-[#685B72] max-w-xl mx-auto mt-3 leading-relaxed">{done.message}</p>
            <div className="inline-flex gap-4 mt-5 text-xs text-[#685B72]">
              <div>Confirmation ID: <span className="font-mono text-[#2D2833]">{done.inquiry_id}</span></div>
              <div>·</div>
              <div>Destination clinic: <span className="text-[#2D2833] font-semibold">{done.clinic}</span></div>
            </div>
          </section>

          {/* ── Printable posters (all 3) ─────────────────────────── */}
          {(posters.provider || posters.family_en || posters.family_es) && (
            <section className="mt-8 bg-white/85 rounded-2xl border border-white/60 shadow-sm p-6 sm:p-8" data-testid="post-submit-posters">
              <h2 className="font-serif text-2xl text-[#2D2833]">Print these QR posters — take them with you</h2>
              <p className="text-sm text-[#685B72] mt-2 leading-relaxed max-w-2xl">
                We designed three ready-to-print posters for you. Post them in your lobby or at your desk — they make it easier for you to refer patients to us and for your patients' families to schedule directly with {BRAND.short}.
              </p>

              <div className="mt-6 grid gap-4">
                {posters.provider && (
                  <PosterCard
                    testId="poster-provider-card"
                    title="Provider referral poster"
                    subtitle="Keep at your desk. Scan the QR anytime you need to send us a new referral — the form pre-fills your practice and clinic."
                    href={posters.provider}
                    filename={`${IS_LUDO ? 'ludo' : 'thrive'}_provider_referral.png`}
                  />
                )}
                {posters.family_en && (
                  <PosterCard
                    testId="poster-family-en-card"
                    title="Family QR poster · English"
                    subtitle={IS_LUDO ? 'Post in your lobby. Parents scan the QR to learn about Ludo ABA and request an ABA assessment.' : "Post in your lobby. Parents scan the QR and book their child's speech evaluation directly with Thrive — no paperwork on your end."}
                    href={posters.family_en}
                    filename={`${IS_LUDO ? 'ludo' : 'thrive'}_family_qr_en.png`}
                  />
                )}
                {posters.family_es && (
                  <PosterCard
                    testId="poster-family-es-card"
                    title="Póster familiar · Español"
                    subtitle={`Igual que el anterior, en español. Las familias hispanohablantes pueden escanear y contactar directamente a ${BRAND.name}.`}
                    href={posters.family_es}
                    filename={`${IS_LUDO ? 'ludo' : 'thrive'}_family_qr_es.png`}
                  />
                )}
              </div>
            </section>
          )}

          {/* ── Bookmark note ─────────────────────────────────────── */}
          {linkUrl && (
            <section className="mt-8 bg-[#BAA9CA]/15 rounded-2xl border border-[#BAA9CA]/30 p-6 sm:p-8" data-testid="post-submit-bookmark">
              <div className="flex items-start gap-3">
                <Bookmark size={22} className="text-[#52475A] flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h2 className="font-serif text-xl text-[#2D2833]">Save this link for your next referral</h2>
                  <p className="text-sm text-[#685B72] mt-2 leading-relaxed">
                    This referral link is <b>yours to keep</b> — it stays active forever. Bookmark it in your browser or paste it into your practice's referral cheat-sheet so you can send new referrals in seconds without asking us to create a new link.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-2 bg-white rounded-lg border border-[#E4DBE8] px-3 py-2">
                    <code className="text-xs sm:text-sm text-[#2D2833] flex-1 break-all font-mono">{linkUrl}</code>
                    <button
                      onClick={copyLink}
                      className="h-8 px-3 rounded-md bg-[#2D2833] text-white text-xs font-semibold hover:bg-[#52475A] transition-colors inline-flex items-center gap-1.5"
                      data-testid="copy-referral-link-btn"
                    >
                      <Copy size={13} /> Copy link
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          <p className="text-[11px] text-[#685B72] text-center mt-8">
            Protected — submissions are stored in a HIPAA-safeguarded environment and only visible to {BRAND.short} intake staff.
          </p>
        </main>
      </div>
    );
  }

  // ─── Main form ───
  return (
    <div className="min-h-screen" style={{ background: PAGE_BG }}>
      <header className="border-b border-white/50 backdrop-blur-md bg-white/70 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <img src={LOGO} alt={BRAND.short} className="w-11 h-11 rounded-full ring-1 ring-[#BAA9CA]/40" />
          <div>
            <div className="font-serif text-xl leading-none text-[#2D2833]">{BRAND.name}</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#685B72] mt-1 font-semibold">Referral intake</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 pb-24">
        <div className="mb-6">
          <h1 className="font-serif text-3xl sm:text-4xl text-[#2D2833]">Refer a patient</h1>
          <p className="text-sm text-[#685B72] mt-2 max-w-2xl leading-relaxed">
            Thank you for referring to {BRAND.short}. Fill out the form below — our intake team will follow up with the family within 2 business days.
          </p>
          {(meta.partner_name || meta.category) && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#BAA9CA]/25 text-[#52475A] px-3 py-1 text-xs font-semibold" data-testid="referral-form-partner-badge">
              <ShieldCheck size={14} /> {meta.partner_name || meta.category} · destination: {meta.clinic}
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="space-y-8">
          <Section title="Patient Information" testId="section-patient">
            <Field label="Patient full name" required testId="field-patient-name">
              <input className={inputCls} value={form.patient_name} onChange={e => upd('patient_name', e.target.value)} data-testid="patient-name-input" />
            </Field>
            <Field label="Patient date of birth" required hint="Format: MM-DD-YYYY" testId="field-patient-dob">
              <input className={inputCls} placeholder="MM-DD-YYYY" value={form.patient_dob} onChange={e => upd('patient_dob', e.target.value)} data-testid="patient-dob-input" />
            </Field>
            <Field label="Parent / Guardian name" required testId="field-guardian-name">
              <input className={inputCls} value={form.guardian_name} onChange={e => upd('guardian_name', e.target.value)} data-testid="guardian-name-input" />
            </Field>
            <Field label="Parent / Guardian phone" required testId="field-guardian-phone">
              <input className={inputCls} type="tel" placeholder="(555) 555-5555" value={form.guardian_phone} onChange={e => upd('guardian_phone', e.target.value)} data-testid="guardian-phone-input" />
            </Field>
          </Section>

          <Section title="Referring Provider" testId="section-provider">
            <Field label="Provider name" required testId="field-provider-name">
              <input className={inputCls} value={form.provider_name} onChange={e => upd('provider_name', e.target.value)} data-testid="provider-name-input" />
            </Field>
            <Field label="Practice or organization" required testId="field-practice-name">
              <input className={inputCls} value={form.practice_name} onChange={e => upd('practice_name', e.target.value)} data-testid="practice-name-input" />
            </Field>
            <Field label="NPI number" testId="field-provider-npi">
              <input className={inputCls} value={form.provider_npi} onChange={e => upd('provider_npi', e.target.value)} data-testid="provider-npi-input" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Provider phone" testId="field-provider-phone">
                <input className={inputCls} type="tel" value={form.provider_phone} onChange={e => upd('provider_phone', e.target.value)} data-testid="provider-phone-input" />
              </Field>
              <Field label="Provider fax" testId="field-provider-fax">
                <input className={inputCls} type="tel" value={form.provider_fax} onChange={e => upd('provider_fax', e.target.value)} data-testid="provider-fax-input" />
              </Field>
            </div>
            <Field label="Provider email (optional)" hint="We'll send you a confirmation once the referral is received." testId="field-provider-email">
              <input className={inputCls} type="email" placeholder="you@practice.com" value={form.provider_email} onChange={e => upd('provider_email', e.target.value)} data-testid="provider-email-input" />
            </Field>
          </Section>

          <Section title="Referral Details" testId="section-referral">
            <Field label="Requested service" required testId="field-service">
              <select className={inputCls} value={form.requested_service} onChange={e => upd('requested_service', e.target.value)} data-testid="requested-service-select">
                {BRAND.services.map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>
            {form.requested_service === 'Other' && (
              <Field label="Please specify" required testId="field-service-other">
                <input className={inputCls} value={form.service_other} onChange={e => upd('service_other', e.target.value)} data-testid="service-other-input" />
              </Field>
            )}
            <Field label="Reason for referral" required hint={BRAND.reasonHint} testId="field-reason">
              <textarea className={textareaCls} value={form.reason_for_referral} onChange={e => upd('reason_for_referral', e.target.value)} data-testid="reason-textarea" />
            </Field>
            <Field label="Relevant medical history" testId="field-history">
              <textarea className={textareaCls} value={form.medical_history} onChange={e => upd('medical_history', e.target.value)} data-testid="history-textarea" />
            </Field>
            <Field label="Diagnosis or ICD-10 code (optional)" testId="field-icd10">
              <input className={inputCls} value={form.icd10_code} onChange={e => upd('icd10_code', e.target.value)} data-testid="icd10-input" />
            </Field>
            <Field label={BRAND.clinicLabel} required hint={BRAND.clinicHint} testId="field-clinic">
              <select className={inputCls} value={form.clinic} onChange={e => upd('clinic', e.target.value)} data-testid="clinic-select">
                {(meta.clinics || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </Section>

          <Section
            title="Insurance Information"
            testId="section-insurance"
            subtitle="Optional — providing insurance details helps our intake team verify benefits before the family calls."
            action={
              <button
                type="button"
                onClick={() => setShowInsurance(v => !v)}
                className="text-sm text-[#685B72] hover:text-[#52475A] underline underline-offset-4"
                data-testid="insurance-toggle"
              >
                {showInsurance ? 'Hide insurance section' : 'Add insurance details'}
              </button>
            }
          >
            {showInsurance && (
              <>
                <Field label="Primary insurance company" testId="field-insurance-company">
                  <input className={inputCls} value={form.insurance_company} onChange={e => upd('insurance_company', e.target.value)} data-testid="insurance-company-input" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Member ID" testId="field-insurance-member-id">
                    <input className={inputCls} value={form.insurance_member_id} onChange={e => upd('insurance_member_id', e.target.value)} data-testid="insurance-member-input" />
                  </Field>
                  <Field label="Group number" testId="field-insurance-group">
                    <input className={inputCls} value={form.insurance_group} onChange={e => upd('insurance_group', e.target.value)} data-testid="insurance-group-input" />
                  </Field>
                </div>
                <Field label="Policyholder full name" testId="field-policyholder-name">
                  <input className={inputCls} value={form.policyholder_name} onChange={e => upd('policyholder_name', e.target.value)} data-testid="policyholder-name-input" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Policyholder DOB" hint="MM-DD-YYYY" testId="field-policyholder-dob">
                    <input className={inputCls} placeholder="MM-DD-YYYY" value={form.policyholder_dob} onChange={e => upd('policyholder_dob', e.target.value)} data-testid="policyholder-dob-input" />
                  </Field>
                  <Field label="Relationship to patient" testId="field-policyholder-relationship">
                    <select className={inputCls} value={form.policyholder_relationship} onChange={e => upd('policyholder_relationship', e.target.value)} data-testid="policyholder-relationship-select">
                      <option value="">Select…</option>
                      <option>Self</option>
                      <option>Parent</option>
                      <option>Guardian</option>
                      <option>Spouse</option>
                      <option>Other</option>
                    </select>
                  </Field>
                </div>
              </>
            )}
          </Section>

          <Section title="Provider Acknowledgment" testId="section-signature">
            <Field label="Type your full name to sign this referral" required testId="field-signature">
              <input className={inputCls} value={form.signature_typed} onChange={e => upd('signature_typed', e.target.value)} data-testid="signature-input" />
            </Field>
            <label className="flex items-start gap-3 rounded-lg border border-[#E4DBE8] bg-white px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.acknowledged}
                onChange={e => upd('acknowledged', e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#685B72]"
                data-testid="ack-checkbox"
              />
              <span className="text-sm text-[#2D2833] leading-relaxed">
                {BRAND.ack}
              </span>
            </label>
          </Section>

          {submitErr && (
            <div className="rounded-lg bg-[#B54B4B]/10 border border-[#B54B4B]/30 text-[#B54B4B] px-4 py-3 text-sm" data-testid="submit-error">
              {submitErr}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="w-full h-12 rounded-xl bg-[#2D2833] text-white text-sm font-semibold hover:bg-[#52475A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            data-testid="submit-referral-btn"
          >
            {submitting ? <><Loader2 className="animate-spin" size={16} /> Submitting…</> : 'Submit referral'}
          </button>
          <p className="text-[11px] text-[#685B72] text-center">
            Protected — submissions are stored in a HIPAA-safeguarded environment and only visible to {BRAND.short} intake staff.
          </p>
        </form>
      </main>
    </div>
  );
}

function Section({ title, subtitle, action, children, testId }) {
  return (
    <section className="bg-white/80 rounded-2xl border border-white/60 shadow-sm p-5 sm:p-6 space-y-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-[#2D2833]">{title}</h2>
          {subtitle && <p className="text-xs text-[#685B72] mt-1 max-w-xl">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function ShellCentered({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: PAGE_BG }}>
      <div className="max-w-md w-full bg-white/80 rounded-2xl border border-white/60 shadow-sm p-8">
        {children}
      </div>
    </div>
  );
}

// One card per poster on the success screen — big Download button so the
// referring provider can save each PNG in a single tap.
function PosterCard({ title, subtitle, href, filename, testId }) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-[#E4DBE8] bg-white p-4 sm:p-5" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-lg text-[#2D2833]">{title}</div>
        <p className="text-sm text-[#685B72] mt-1 leading-relaxed">{subtitle}</p>
      </div>
      <a
        href={href}
        download={filename}
        target="_blank"
        rel="noreferrer"
        className="flex-shrink-0 h-11 px-4 rounded-lg bg-[#2D2833] text-white text-sm font-semibold hover:bg-[#52475A] transition-colors inline-flex items-center gap-2"
        data-testid={`${testId}-download-btn`}
      >
        <FileDown size={16} /> Download
      </a>
    </div>
  );
}